import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { db } from '@/lib/firebase';
import { collection, query, where, getDocs, orderBy, doc, updateDoc, deleteDoc, Timestamp, onSnapshot, limit, startAfter, getCountFromServer } from 'firebase/firestore';
import { useToast } from '@/components/ui/use-toast';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Search, Phone, User, Mail, MessageSquare, Trash2, CheckCircle2, XCircle, Clock, Download, Printer, Bell, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import * as XLSX from 'xlsx';

interface Booking {
  id: string;
  fullName: string;
  phone: string;
  email: string;
  age: string;
  gender: string;
  medicalHistory: string;
  allergies: string;
  currentMedications: string;
  previousDentalTreatment: string;
  preferredDate: string;
  preferredTime: string;
  serviceType: string;
  notes: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  createdAt: Date;
  updatedAt: Date;
}

const serviceTypes = [
  "كشف أولي",
  "كشف متابعة",
  "تنظيف أسنان",
  "حشو",
  "علاج عصب",
  "خلع",
  "تقويم",
  "تبييض",
  "تركيب",
  "زراعة",
  "أشعة",
  "أخرى"
];

export default function Appointments() {
  const { toast } = useToast();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'confirmed' | 'cancelled'>('all');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [isViewingDetails, setIsViewingDetails] = useState(false);
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: new Date(),
    to: new Date()
  });
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const BOOKINGS_PER_PAGE = 10;

  // Request notification permission
  const requestNotificationPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast({
          title: "تم تفعيل الإشعارات",
          description: "ستصلك إشعارات عند وصول حجوزات جديدة"
        });
      } else {
        toast({
          variant: "destructive",
          title: "لم يتم تفعيل الإشعارات",
          description: "يرجى السماح بالإشعارات في إعدادات المتصفح"
        });
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };

  // Show browser notification
  const showNotification = (booking: Booking) => {
    if (!notificationsEnabled) return;

    const notification = new Notification('حجز جديد', {
      body: `تم استلام حجز جديد من ${booking.fullName}`,
      icon: '/logo.png',
      badge: '/logo.png',
      tag: 'new-booking'
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  };

  // Fetch initial bookings with pagination
  const fetchInitialBookings = useCallback(async () => {
    try {
      setIsLoading(true);
      const bookingsRef = collection(db, 'bookings');
      
      // Get total count
      const countSnapshot = await getCountFromServer(bookingsRef);
      setTotalCount(countSnapshot.data().count);

      // Get first page
      const q = query(
        bookingsRef,
        orderBy('createdAt', 'desc'),
        limit(BOOKINGS_PER_PAGE)
      );

      const querySnapshot = await getDocs(q);
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastDoc(lastVisible);
      setHasMore(querySnapshot.docs.length === BOOKINGS_PER_PAGE);

      const bookingsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
        } as Booking;
      });

      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء جلب الحجوزات"
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load more bookings
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;

    try {
      setIsLoading(true);
      const bookingsRef = collection(db, 'bookings');
      const q = query(
        bookingsRef,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(BOOKINGS_PER_PAGE)
      );

      const querySnapshot = await getDocs(q);
      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastDoc(lastVisible);
      setHasMore(querySnapshot.docs.length === BOOKINGS_PER_PAGE);

      const newBookings = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()
        } as Booking;
      });

      setBookings(prev => [...prev, ...newBookings]);
    } catch (error) {
      console.error('Error loading more bookings:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء تحميل المزيد من الحجوزات"
      });
    } finally {
      setIsLoading(false);
    }
  }, [lastDoc, hasMore, isLoading]);

  // Set up real-time listener for new bookings only
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }

    const bookingsRef = collection(db, 'bookings');
    const q = query(bookingsRef, orderBy('createdAt', 'desc'), limit(1));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const newBooking = {
            id: change.doc.id,
            ...change.doc.data(),
            createdAt: change.doc.data().createdAt instanceof Timestamp 
              ? change.doc.data().createdAt.toDate() 
              : new Date(),
            updatedAt: change.doc.data().updatedAt instanceof Timestamp 
              ? change.doc.data().updatedAt.toDate() 
              : new Date()
          } as Booking;

          if (notificationsEnabled) {
            showNotification(newBooking);
          }

          toast({
            title: "حجز جديد",
            description: `تم استلام حجز جديد من ${newBooking.fullName}`,
            duration: 5000
          });

          // Update total count
          setTotalCount(prev => prev + 1);
        }
      });
    });

    return () => unsubscribe();
  }, [notificationsEnabled]);

  // Initial fetch
  useEffect(() => {
    fetchInitialBookings();
  }, [fetchInitialBookings]);

  // Filter bookings
  useEffect(() => {
    let filtered = [...bookings];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.fullName.toLowerCase().includes(query) ||
        booking.phone.includes(query) ||
        booking.email.toLowerCase().includes(query) ||
        booking.serviceType.toLowerCase().includes(query)
      );
    }

    setFilteredBookings(filtered);
  }, [bookings, searchQuery, statusFilter]);

  const handleStatusChange = async (bookingId: string, newStatus: 'confirmed' | 'cancelled') => {
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      toast({
        title: "تم التحديث",
        description: `تم ${newStatus === 'confirmed' ? 'تأكيد' : 'إلغاء'} الحجز بنجاح`
      });

      fetchInitialBookings();
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث حالة الحجز"
      });
    }
  };

  const handleDelete = async (bookingId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا الحجز؟')) return;

    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await deleteDoc(bookingRef);
      
      toast({
        title: "تم الحذف",
        description: "تم حذف الحجز بنجاح"
      });

      fetchInitialBookings();
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء حذف الحجز"
      });
    }
  };

  const exportToExcel = () => {
    const data = filteredBookings.map(booking => ({
      'الاسم': booking.fullName,
      'رقم الهاتف': booking.phone,
      'البريد الإلكتروني': booking.email,
      'العمر': booking.age,
      'الجنس': booking.gender,
      'نوع الخدمة': booking.serviceType,
      'التاريخ المفضل': booking.preferredDate,
      'الوقت المفضل': booking.preferredTime,
      'الحالة': booking.status === 'pending' ? 'قيد الانتظار' :
                booking.status === 'confirmed' ? 'مؤكد' : 'ملغي',
      'تاريخ الإنشاء': format(booking.createdAt, 'dd MMMM yyyy - hh:mm a', { locale: ar })
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'حجوزات الموقع');
    XLSX.writeFile(wb, `حجوزات_الموقع_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const printReport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const content = `
      <html dir="rtl">
        <head>
          <title>تقرير حجوزات الموقع</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: right; }
            th { background-color: #f5f5f5; }
            @media print {
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>تقرير حجوزات الموقع</h1>
            <p>تاريخ التقرير: ${format(new Date(), 'dd MMMM yyyy', { locale: ar })}</p>
          </div>

          <div className="overflow-x-auto p-2">
            <table className="min-w-full text-sm sm:text-xs">
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>رقم الهاتف</th>
                  <th>البريد الإلكتروني</th>
                  <th>نوع الخدمة</th>
                  <th>التاريخ المفضل</th>
                  <th>الوقت المفضل</th>
                  <th>الحالة</th>
                  <th>تاريخ الإنشاء</th>
                </tr>
              </thead>
              <tbody>
                ${filteredBookings.map(booking => `
                  <tr>
                    <td>${booking.fullName}</td>
                    <td>${booking.phone}</td>
                    <td>${booking.email}</td>
                    <td>${booking.serviceType}</td>
                    <td>${booking.preferredDate}</td>
                    <td>${booking.preferredTime}</td>
                    <td>${booking.status === 'pending' ? 'قيد الانتظار' :
                         booking.status === 'confirmed' ? 'مؤكد' : 'ملغي'}</td>
                    <td>${format(booking.createdAt, 'dd MMMM yyyy - hh:mm a', { locale: ar })}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <div class="no-print" style="margin-top: 20px; text-align: center;">
            <button onclick="window.print()">طباعة التقرير</button>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(content);
    printWindow.document.close();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent animate-gradient mb-2 md:mb-0">
            حجوزات الموقع
          </h1>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-4 w-full">
            {!notificationsEnabled && (
              <Button
                variant="outline"
                onClick={requestNotificationPermission}
                className="flex items-center gap-2 w-full sm:w-auto"
              >
                <Bell className="w-4 h-4" />
                تفعيل الإشعارات
              </Button>
            )}
            <div className="flex items-center gap-2 relative group w-full sm:w-auto">
              <Input
                placeholder="بحث..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-[200px] transition-all duration-300 focus:w-[250px]"
              />
              <Search className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors absolute right-2 top-1/2 -translate-y-1/2" />
            </div>
            <Select 
              value={statusFilter} 
              onValueChange={(value: 'all' | 'pending' | 'confirmed' | 'cancelled') => setStatusFilter(value)}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="حالة الحجز" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحجوزات</SelectItem>
                <SelectItem value="pending">قيد الانتظار</SelectItem>
                <SelectItem value="confirmed">مؤكد</SelectItem>
                <SelectItem value="cancelled">ملغي</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              onClick={exportToExcel} 
              className="flex items-center gap-2 w-full sm:w-auto"
            >
              <Download className="w-4 h-4" />
              تصدير Excel
            </Button>
            <Button 
              variant="outline" 
              onClick={printReport} 
              className="flex items-center gap-2 w-full sm:w-auto"
            >
              <Printer className="w-4 h-4" />
              طباعة التقرير
            </Button>
          </div>
        </div>

        <Card className="shadow-modern">
          <CardContent className="pt-6">
            <div className="grid gap-6">
              {filteredBookings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  لا توجد حجوزات
                </div>
              ) : (
                <>
                  {filteredBookings.map((booking) => (
                    <Card 
                      key={booking.id} 
                      className="hover:shadow-lg transition-all duration-300 hover:scale-[1.02] w-full"
                    >
                      <CardHeader className="pb-2">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
                          <div className="flex items-center gap-3 w-full">
                            <div className="bg-primary/10 p-2 rounded-full">
                              <User className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <CardTitle className="text-sm sm:text-base font-semibold">
                                {booking.fullName}
                              </CardTitle>
                              <p className="text-xs sm:text-sm text-muted-foreground">
                                {booking.serviceType}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-2 sm:mt-0">
                            <Badge
                              variant={
                                booking.status === 'confirmed'
                                  ? 'default'
                                  : booking.status === 'cancelled'
                                  ? 'destructive'
                                  : 'secondary'
                              }
                              className="text-[10px] sm:text-xs"
                            >
                              {booking.status === 'pending'
                                ? 'قيد الانتظار'
                                : booking.status === 'confirmed'
                                ? 'مؤكد'
                                : 'ملغي'}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedBooking(booking);
                                setIsViewingDetails(true);
                              }}
                              className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-primary/10 transition-colors"
                            >
                              <MessageSquare className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            </Button>
                            {booking.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleStatusChange(booking.id, 'confirmed')}
                                  className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-green-500/10 hover:text-green-500 transition-colors"
                                >
                                  <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleStatusChange(booking.id, 'cancelled')}
                                  className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-red-500/10 hover:text-red-500 transition-colors"
                                >
                                  <XCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(booking.id)}
                              className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-destructive/10 hover:text-destructive transition-colors"
                            >
                              <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                          <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground" />
                            <span>{booking.phone}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground" />
                            <span>{booking.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground" />
                            <span>{booking.preferredDate}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground" />
                            <span>{booking.preferredTime}</span>
                          </div>
                          {booking.notes && (
                            <div className="col-span-1 sm:col-span-2 flex items-start gap-2">
                              <MessageSquare className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground mt-1" />
                              <span className="text-[10px] sm:text-xs text-muted-foreground line-clamp-2">
                                {booking.notes}
                              </span>
                            </div>
                          )}
                          <div className="col-span-1 sm:col-span-2 text-[10px] sm:text-xs text-muted-foreground">
                            {format(booking.createdAt, 'dd MMMM yyyy - hh:mm a', { locale: ar })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {hasMore && (
                    <div className="flex justify-center py-4">
                      <Button
                        variant="outline"
                        onClick={loadMore}
                        disabled={isLoading}
                        className="flex items-center gap-2"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            جاري التحميل...
                          </>
                        ) : (
                          'تحميل المزيد'
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Booking Details Dialog */}
        <Dialog open={isViewingDetails} onOpenChange={setIsViewingDetails}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                تفاصيل الحجز
              </DialogTitle>
            </DialogHeader>
            {selectedBooking && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>الاسم</Label>
                  <p className="text-sm">{selectedBooking.fullName}</p>
                </div>
                <div>
                  <Label>رقم الهاتف</Label>
                  <p className="text-sm">{selectedBooking.phone}</p>
                </div>
                <div>
                  <Label>البريد الإلكتروني</Label>
                  <p className="text-sm">{selectedBooking.email}</p>
                </div>
                <div>
                  <Label>العمر</Label>
                  <p className="text-sm">{selectedBooking.age}</p>
                </div>
                <div>
                  <Label>الجنس</Label>
                  <p className="text-sm">{selectedBooking.gender}</p>
                </div>
                <div>
                  <Label>نوع الخدمة</Label>
                  <p className="text-sm">{selectedBooking.serviceType}</p>
                </div>
                <div>
                  <Label>التاريخ المفضل</Label>
                  <p className="text-sm">{selectedBooking.preferredDate}</p>
                </div>
                <div>
                  <Label>الوقت المفضل</Label>
                  <p className="text-sm">{selectedBooking.preferredTime}</p>
                </div>
                <div className="col-span-2">
                  <Label>التاريخ الطبي</Label>
                  <p className="text-sm">{selectedBooking.medicalHistory || 'لا يوجد'}</p>
                </div>
                <div className="col-span-2">
                  <Label>الحساسية</Label>
                  <p className="text-sm">{selectedBooking.allergies || 'لا يوجد'}</p>
                </div>
                <div className="col-span-2">
                  <Label>الأدوية الحالية</Label>
                  <p className="text-sm">{selectedBooking.currentMedications || 'لا يوجد'}</p>
                </div>
                <div className="col-span-2">
                  <Label>العلاجات السابقة</Label>
                  <p className="text-sm">{selectedBooking.previousDentalTreatment || 'لا يوجد'}</p>
                </div>
                <div className="col-span-2">
                  <Label>ملاحظات إضافية</Label>
                  <p className="text-sm">{selectedBooking.notes || 'لا يوجد'}</p>
                </div>
                <div className="col-span-2">
                  <Label>حالة الحجز</Label>
                  <Badge
                    variant={
                      selectedBooking.status === 'confirmed'
                        ? 'default'
                        : selectedBooking.status === 'cancelled'
                        ? 'destructive'
                        : 'secondary'
                    }
                  >
                    {selectedBooking.status === 'pending'
                      ? 'قيد الانتظار'
                      : selectedBooking.status === 'confirmed'
                      ? 'مؤكد'
                      : 'ملغي'}
                  </Badge>
                </div>
                <div className="col-span-2">
                  <Label>تاريخ الإنشاء</Label>
                  <p className="text-sm">
                    {format(selectedBooking.createdAt, 'dd MMMM yyyy - hh:mm a', { locale: ar })}
                  </p>
                </div>
                <div className="col-span-2">
                  <Label>آخر تحديث</Label>
                  <p className="text-sm">
                    {format(selectedBooking.updatedAt, 'dd MMMM yyyy - hh:mm a', { locale: ar })}
                  </p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
} 